import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import some from 'lodash/some';
import omitBy from 'lodash/omitBy';
import range from 'lodash/range';

import {ProjectFeatureFlag, ValueRepresentationType} from 'common/report/constants';
import {SerializePropFilterContext} from 'common/report/util/arb';
import {featuresStore} from 'common/report/stores/features';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {ComparisonDate} from 'common/widgets/query-block/time-comparison-menu/types';
import {
  comparisonDateFromBookmarkFormat,
  comparisonDateToBookmarkFormat,
} from 'common/widgets/query-block/time-comparison-menu/utils';
import {PropertyFilter, PropertyFilterAttributes} from 'common/widgets/property-filter-menu/models/property-filter';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {isOtherSegment, Segment} from 'common/widgets/query-block/segment-menu/types';
import {FunnelAttribution} from 'common/widgets/query-builder/types';
import {bookmarkFilterByCohortToCohortFilters, getFilterByCohorts} from 'common/widgets/query-block/filter-menu/util';
import {
  isGroupedEntry,
  QueryEventEntry,
  QueryGroupedEntry,
  QuerySuperEntry,
} from 'common/widgets/query-entry-section/types';
import {
  bookmarkGroupByToGroupBy,
  groupByToBookmarkGroupBy,
  isGroupByEqual,
} from 'common/widgets/query-block/segment-menu/util';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {
  BucketChartVizType,
  ChartType,
  ConversionWindow,
  ConversionWindowUnit,
  CountType,
  FunnelBookmark,
  FunnelOrder,
  SelectedColumns,
  SelectMode,
  TableToggleableMetrics,
  TrendChartType,
  TrendUnit,
} from 'common/report/models/funnels/types';
import {
  mapBookmarkExclusionStepToEventEntry,
  mapBookmarkStepsToEntries,
  mapBookmarkToAttribution,
  mapBookmarkToHoldPropConstant,
  mapEntriesToBookmarkSteps,
  mapEventEntryToBookmarkExclusionStep,
  mapHoldPropConstantToBookmark,
} from 'common/report/models/funnels/format';
import {BucketDefinition, TimeBucketDefinition} from 'common/report/models/funnels/bar-chart/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {fromEnum, getAllowedTrendUnits, getTimeBucketsFromConversionWindow} from 'common/report/util/funnels/utils';
import {FrequencyViewType} from 'common/report/models/funnels/frequency-chart/types';
import {Segmentation} from 'common/widgets/property-filter-menu/models/segfilter';

type Comparator<T = any> = (a: T, b: T, forQuery?: boolean) => boolean;

/** comparator that returns equivalent forQuery every time */
function notForQuery<T>(fallback: Comparator<T>): Comparator<T> {
  return function (a: T, b: T, forQuery?: boolean) {
    if (forQuery) {
      return true;
    }
    return fallback(a, b, forQuery);
  };
}

/** Utility to generate a comparison function using .equals */
function dotEquals<T extends {equals(o: T): boolean}>(a: T, b: T): boolean {
  return a.equals(b);
}

function tripleEquals(a: any, b: any) {
  return a === b;
}

function tripleEqualsIfFalseyElse<T>(fallback: Comparator<T>) {
  return function (a, b, forQuery) {
    if (!(a && b)) {
      return a === b;
    } else {
      return fallback(a, b, forQuery);
    }
  };
}

function listsEqual<T>(comparator: Comparator<T>) {
  return function compareLists(la: T[], lb: T[], forQuery?: boolean): boolean {
    return la.length === lb.length && la.every((_, index) => comparator(la[index], lb[index]), forQuery);
  };
}

const filtersEqual = listsEqual(dotEquals);

/**
 * Determine if two event entries are eqivalent
 * @param {boolean} forQuery If it results in a difference in the query & results.
 */
function eventsEqual(
  eventA: QueryEventEntry | QueryGroupedEntry,
  eventB: QueryEventEntry | QueryGroupedEntry,
  forQuery: boolean,
): boolean {
  if (!eventA || !eventB) {
    return false;
  }

  if (isGroupedEntry(eventA) || isGroupedEntry(eventB)) {
    if (!isGroupedEntry(eventA) || !isGroupedEntry(eventB)) {
      return false;
    }

    return (
      isEqual(omit(eventA, [`event`]), omit(eventB, [`event`])) &&
      listsEqual(queryEventEntryEqual)(eventA.event, eventB.event, forQuery)
    );
  } else {
    return queryEventEntryEqual(eventA, eventB, forQuery);
  }
}

function queryEventEntryEqual(eventA: QueryEventEntry, eventB: QueryEventEntry, forQuery: boolean): boolean {
  if (!eventA || !eventB) {
    return false;
  }

  const omitFields = forQuery ? [`name`, `label`, `renamed`] : [`name`, `label`];
  const [compareA, compareB] = [eventA, eventB].map((entry) => omit(entry.event, omitFields));

  return (
    // if under two filters, the operator doesn't matter
    (eventA.filters.length < 2 || eventA.filtersOperator === eventB.filtersOperator) &&
    Boolean(eventA.entryIndexLabelToggle) === Boolean(eventB.entryIndexLabelToggle) &&
    isEqual(compareA, compareB) &&
    filtersEqual(eventA.filters, eventB.filters)
  );
}

function exclusionStepsEqual(a: QueryEventEntry, b: QueryEventEntry, forQuery: boolean) {
  return (
    queryEventEntryEqual(a, b, forQuery) && isEqual(a.excludeRange, b.excludeRange) && a.excludeAll === b.excludeAll
  );
}

const EQUIVALENCY_EVALUATORS: {[key: string]: Comparator} = Object.freeze({
  funnelAttributionSelection: isEqual,
  cohortFilters: filtersEqual,
  conversionWindow: isEqual,
  countType: tripleEquals,
  dataGroupId: tripleEquals,
  dateRange: tripleEqualsIfFalseyElse(dotEquals),
  comparedDateRange: tripleEqualsIfFalseyElse(isEqual),
  eventFilters: filtersEqual,
  events: listsEqual(eventsEqual),
  exclusionSteps: listsEqual(exclusionStepsEqual),
  groupBy: isGroupByEqual,
  controlGroup: isEqual,
  holdingPropertyConstant: isEqual,
  userFilters: filtersEqual,
  timeBuckets: isEqual,
  frequencyBuckets: isEqual,
  trendUnit: tripleEquals,
  funnelOrder: tripleEquals,
  chartType: tripleEquals,
  selectedTableColumns: notForQuery(isEqual),
});

const DEFAULT_CONV_WINDOW = {
  unit: ConversionWindowUnit.Day,
  value: 30,
};

const SELECTED_TABLE_COLUMN_OPTIONS: SelectedColumns = {
  [TableToggleableMetrics.StatSig]: true,
  [TableToggleableMetrics.ConvPrevStep]: true,
  [TableToggleableMetrics.ConvFirstStep]: false,
  [TableToggleableMetrics.Count]: true,
  [TableToggleableMetrics.TimePrevStep]: true,
  [TableToggleableMetrics.TimeFirstStep]: false,
};

const FUNNELS_REPORT_DEFAULTS: Partial<FunnelsQuery> = Object.freeze({
  chartType: ChartType.Chart,
  countType: CountType.Unique,
  conversionWindow: DEFAULT_CONV_WINDOW,
  dataGroupId: null,
  dateRange: new FullLengthDateRange().getAttributes(),
  comparedDateRange: null,
  cohortFilters: [],
  eventFilters: [],
  events: [],
  exclusionSteps: [],
  groupBy: [],
  holdingPropertyConstant: [],
  timeBuckets: getTimeBucketsFromConversionWindow(DEFAULT_CONV_WINDOW),
  frequencyBuckets: {min: 1, max: 20, bucketSize: 1, offset: 0},
  trendUnit: TrendUnit.Day,
  funnelOrder: FunnelOrder.Loose,
  userFilters: [],
  controlGroup: [],
  funnelAttributionSelection: {action_mode: `last_touch`},
  selectedTableColumns: SELECTED_TABLE_COLUMN_OPTIONS,

  projectId: -1,
  utcOffset: 0,
  version: 2,
});

type ExtraBookmarkParameters = Partial<{
  eventIndex: number;
  selected: ColorChoice[];
  selectMode: SelectMode;
  trendType: TrendChartType;
  frequencyType: FrequencyViewType;
  frequencyVizType: BucketChartVizType;
  ttcVizType: BucketChartVizType;
  ttcValueRepresentation: ValueRepresentationType;
  frequencyValueRepresentation: ValueRepresentationType;
  sortField: string;
  sortOrder: number;
}>;

export class FunnelsQuery {
  /**
   * Convert from bookmark to funnel query, reverse of "generateBookmark"
   * @param params {Object} bookmark params
   * @param customEvents {CustomEventDefinition[]} list of supported custom events.
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  public static fromBookmark(
    params: FunnelBookmark,
    {
      customEvents,
      projectId,
      utcOffset,
    }: {
      customEvents: CustomEventDefinition[];
      projectId: number;
      utcOffset: number;
    },
  ) {
    const attrs: Partial<FunnelsQuery> = {projectId, utcOffset};

    if (params.steps) {
      attrs.events = mapBookmarkStepsToEntries(customEvents, params.steps);
    }
    if (params.exclusions) {
      attrs.exclusionSteps = params.exclusions.map(
        mapBookmarkExclusionStepToEventEntry.bind(null, attrs.events.length, customEvents),
      );
    }
    if (params.aggregate_by) {
      attrs.holdingPropertyConstant = mapBookmarkToHoldPropConstant(params.aggregate_by);
    }
    if (params.length && params.length_unit) {
      attrs.conversionWindow = {
        value: parseInt((params.length as unknown) as string, 10),
        unit: params.length_unit,
      };
      // TODO (dtwoodall): Old default date range logic, we should update this with something smarter or remove it entirely
      let dateWindow = 30;
      if (attrs.conversionWindow.value > 30 && attrs.conversionWindow.unit === `day`) {
        dateWindow = 90;
      }
      attrs.dateRange = new FullLengthDateRange({
        type: DateRangeType.RelativeAfter,
        window: {
          unit: TimeUnit.Day,
          value: dateWindow,
        },
      }).getAttributes();
    }
    if (params.time_buckets) {
      attrs.timeBuckets = {
        min: params.time_buckets.min,
        max: params.time_buckets.max,
        bucketSize: params.time_buckets.bucket_size,
        isDrilledDown: params.time_buckets.is_drilled_down,
        offset: params.time_buckets.offset,
      };
    }
    if (params.frequency_buckets) {
      attrs.frequencyBuckets = {
        min: params.frequency_buckets.min,
        max: params.frequency_buckets.max,
        bucketSize: params.frequency_buckets.bucket_size,
        offset: params.frequency_buckets.offset,
      };
    }
    if (params.group_by) {
      attrs.groupBy = bookmarkGroupByToGroupBy(params.group_by);
    }

    // TODO: Split this into cohortFilter and userFilters when we switch over to the new API
    attrs.cohortFilters = bookmarkFilterByCohortToCohortFilters(params.filter_by_cohort);

    if (params.date_range) {
      attrs.dateRange = FullLengthDateRange.fromBookmarkFormat(params.date_range).getAttributes();
    }
    if (params.time_comparison) {
      attrs.comparedDateRange = params.time_comparison
        ? comparisonDateFromBookmarkFormat(params.time_comparison)
        : null;
    }
    if (params.filter_by_event) {
      type PartitionedFilters = [Array<{event: PropertyFilter}>, Array<{user: PropertyFilter}>];
      const filters = partition(params.filter_by_event.children, `event`) as PartitionedFilters;
      attrs.eventFilters = filters[0].map((filter) => filter.event);
      attrs.userFilters = filters[1].map((filter) => filter.user);
    }
    if (params.count_type) {
      attrs.countType = fromEnum(params.count_type, CountType, CountType.Unique);
    }
    if (params.data_group_id) {
      attrs.dataGroupId = params.data_group_id;
    }
    if (params.trend_unit) {
      attrs.trendUnit = params.trend_unit;
    }
    if (params.chart_type) {
      attrs.chartType = fromEnum(params.chart_type, ChartType, ChartType.Chart);
    }

    if (params.funnel_order) {
      attrs.funnelOrder = fromEnum(params.funnel_order, FunnelOrder, FunnelOrder.Loose);
    }

    if (params.action_mode) {
      attrs.funnelAttributionSelection = mapBookmarkToAttribution(params.action_mode, params.action_step);
    }

    if (params.selectedTableColumns) {
      attrs.selectedTableColumns = params.selectedTableColumns;
    }

    let setStepAnyOrder = false;
    if (params.is_step_any_order && params.is_step_any_order.length === attrs.events.length) {
      attrs.events = attrs.events.map((event, idx) => ({
        ...event,
        entryIndexLabelToggle: params.is_step_any_order[idx],
      }));
      setStepAnyOrder = true;
    }

    // Support older bookmarks that do not store is_step_any_order information
    if (attrs.funnelOrder === FunnelOrder.Any && !setStepAnyOrder) {
      attrs.events.map((event) => (event.entryIndexLabelToggle = true));
      attrs.events[0].entryIndexLabelToggle = false;
    }

    return new FunnelsQuery(attrs);
  }

  public static createFromUpdate(
    oldQuery: FunnelsQuery,
    newParams: Partial<FunnelsQuery>,
    additionalFilters: PropertyFilterAttributes[],
  ) {
    const {cohortFilters, eventFilters, userFilters} = {
      ...oldQuery,
      ...newParams,
    };
    return new FunnelsQuery({
      ...oldQuery,
      ...newParams,
      ...(additionalFilters
        ? {
            cohortFilters: [
              ...(cohortFilters || []),
              ...additionalFilters.filter((f) => f.resourceType === `cohort`).map((f) => new PropertyFilter(f)),
            ],
            eventFilters: [
              ...(eventFilters || []),
              ...additionalFilters.filter((f) => f.resourceType === `event`).map((f) => new PropertyFilter(f)),
            ],
            userFilters: [
              ...(userFilters || []),
              ...additionalFilters.filter((f) => f.resourceType === `user`).map((f) => new PropertyFilter(f)),
            ],
          }
        : {}),
    });
  }

  public version: number;
  public countType: CountType;
  public funnelAttributionSelection: FunnelAttribution;
  public conversionWindow: ConversionWindow;
  public timeBuckets: TimeBucketDefinition;
  public frequencyBuckets: BucketDefinition;
  public dataGroupId: string;
  public dateRange: Partial<FullLengthDateRange>;
  public comparedDateRange: ComparisonDate;
  public cohortFilters: PropertyFilter[];
  public eventFilters: PropertyFilter[];
  public events: Array<QueryEventEntry | QueryGroupedEntry>;
  public exclusionSteps: QueryEventEntry[];
  public groupBy: Segment[];
  public holdingPropertyConstant: Segmentation[];
  public trendUnit: TrendUnit;
  public funnelOrder: FunnelOrder;
  public controlGroup: string[];
  public selectedTableColumns: SelectedColumns;
  public querySampling: boolean;

  public userFilters: PropertyFilter[];
  public chartType: ChartType;

  // Global config that has leaked through to this class for serialization purposes.
  public projectId: number;
  public utcOffset: number;

  /**
   * @param [attrs] - Attributes to set on the new FunnelsQuery object
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  constructor(attrs: Partial<FunnelsQuery> = {}) {
    if (`conversionWindow` in attrs && (!attrs.timeBuckets || !attrs.timeBuckets.isDrilledDown)) {
      // replace the time buckets if conversion window is being updated. (except when drilled down)
      attrs.timeBuckets = getTimeBucketsFromConversionWindow(attrs.conversionWindow);
    }

    if (`funnelOrder` in attrs) {
      if (attrs.funnelOrder === FunnelOrder.Any) {
        if (!some(attrs.events.map((entry) => entry.entryIndexLabelToggle))) {
          attrs.funnelOrder = FunnelOrder.Loose;
        }
        if (attrs.groupBy && attrs.groupBy.length > 0) {
          const idx = attrs.groupBy.findIndex(isOtherSegment);
          if (idx >= 0) {
            attrs.groupBy.push(attrs.groupBy.splice(idx, 1)[0]);
          }
        }
      }
      if (attrs.funnelOrder === FunnelOrder.Loose) {
        if (attrs.groupBy) {
          attrs.groupBy = attrs.groupBy.filter(
            (grouping: Segment) => Array.isArray(grouping) || !(`other` in grouping),
          );
        }
        if (attrs.chartType === ChartType.Sankey) {
          attrs.chartType = ChartType.Chart;
        }
        attrs.events = attrs.events.map((entry) => ({...entry, entryIndexLabelToggle: false}));
      }
    }

    let funnelsDefaults = {...FUNNELS_REPORT_DEFAULTS};
    // Dynamic defaults
    if (featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.ShowLast7Days)) {
      const conversionWindow = {
        unit: ConversionWindowUnit.Day,
        value: 7,
      };

      funnelsDefaults = {
        ...funnelsDefaults,
        dateRange: new FullLengthDateRange({
          type: DateRangeType.RelativeAfter,
          window: {
            unit: TimeUnit.Day,
            value: 7,
          },
        }).getAttributes(),
        conversionWindow,
        timeBuckets: getTimeBucketsFromConversionWindow(conversionWindow),
      };
    }

    if (featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.QTSByDefault)) {
      this.querySampling = true;
    }

    Object.assign(
      this,
      funnelsDefaults,
      omitBy(attrs, function omitEmptyStrings(value) {
        return value === ``;
      }),
    );

    // Instantiate models from primitive attrs
    this.dateRange = new FullLengthDateRange(this.dateRange);
    const arr = getAllowedTrendUnits(this.dateRange.getIntervalDays());
    if (!arr.includes(this.trendUnit)) {
      this.trendUnit = arr.includes(TrendUnit.Day) ? TrendUnit.Day : arr[0];
    }
    this.events = this.events.map((entry) => {
      if (isGroupedEntry(entry)) {
        entry.event = entry.event.map((nestedEntry) => ({
          ...nestedEntry,
          filters: constructPropertyFilters(nestedEntry.filters),
        }));
      }
      return {...entry, filters: constructPropertyFilters(entry.filters)};
    });
    this.exclusionSteps = this.exclusionSteps.map((entry) => ({
      ...entry,
      filters: constructPropertyFilters(entry.filters),
    }));

    // force filter initialization
    // eslint-disable-next-line no-self-assign
    this.filters = this.filters;
  }

  get filters(): PropertyFilter[] {
    return [...this.eventFilters, ...this.userFilters, ...this.cohortFilters];
  }

  set filters(filters) {
    this.eventFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Event));
    this.userFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.User));
    this.cohortFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Cohort));
  }

  /**
   * Determine if this funnels query is equal to another funnels query
   * @param {FunnelsQuery} otherQuery The other query to compare this query to
   * @returns {boolean}
   */
  public equals(otherQuery) {
    // Assign to a temporary variable for easy debugging.
    const notEqual = Object.entries(EQUIVALENCY_EVALUATORS).filter(([attr, fn]) => {
      return !fn(this[attr], otherQuery[attr], false);
    });

    return notEqual.length === 0;
  }

  /**
   * Determine if this funnels query is equal to another funnels query in terms of requiring a re-query.
   * (i.e. will it generate a query that will return different results)
   * @param {FunnelsQuery} otherQuery The other query to compare this query to
   * @returns {boolean}
   */
  public generatedQueryEquals(otherQuery) {
    // Assign to a temporary variable for easy debugging.
    const notEqual = Object.entries(EQUIVALENCY_EVALUATORS).filter(([attr, fn]) => {
      return !fn(this[attr], otherQuery[attr], true);
    });

    return notEqual.length === 0;
  }

  /** generate a bookmark object that can make a query and be persisted as a saved report */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  public generateBookmark(extraParams?: ExtraBookmarkParameters): FunnelBookmark {
    const aggregateBy = mapHoldPropConstantToBookmark(this.holdingPropertyConstant);
    const propertyConfig = this.getPropertySerializerConfig();
    const steps = mapEntriesToBookmarkSteps(propertyConfig, this.events);
    const exclusions = this.exclusionSteps.map((step) =>
      mapEventEntryToBookmarkExclusionStep(this.events.length, propertyConfig, step),
    );
    const groupBy = groupByToBookmarkGroupBy(this.groupBy);
    const arr = getAllowedTrendUnits(this.dateRange.getIntervalDays());
    const comparedDateRange = this.comparedDateRange ? comparisonDateToBookmarkFormat(this.comparedDateRange) : null;

    if (!arr.includes(this.trendUnit)) {
      this.trendUnit = arr[0];
    }
    const params: FunnelBookmark = {
      ...(aggregateBy.length ? {aggregate_by: aggregateBy} : {}),
      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
      ...(this.controlGroup ? {stat_sig_control: this.controlGroup} : {}),
      chart_type: this.chartType,
      count_type: this.countType,
      date_range: this.dateRange.getBookmarkFormat(),
      time_comparison: comparedDateRange,
      exclusions,
      filter_by_cohort: JSON.stringify(this.cohortFilters.length ? getFilterByCohorts(this.cohortFilters) : {}),
      length: this.conversionWindow.value,
      length_unit: this.conversionWindow.unit,
      time_buckets: {
        min: this.timeBuckets.min,
        max: this.timeBuckets.max,
        offset: this.timeBuckets.offset,
        bucket_size: this.timeBuckets.bucketSize,
        is_drilled_down: this.timeBuckets.isDrilledDown,
      },
      frequency_buckets: {
        min: this.frequencyBuckets.min,
        max: this.frequencyBuckets.max,
        offset: this.frequencyBuckets.offset,
        bucket_size: this.frequencyBuckets.bucketSize,
      },
      trend_unit: this.trendUnit,
      funnel_order: this.funnelOrder,
      version: this.version,
      steps,
      selectedSegments: extraParams ? extraParams.selected : [],
      selectMode: extraParams ? extraParams.selectMode : SelectMode.Position,
      selectedTableColumns: this.selectedTableColumns,
    };

    // action mode and action steps are set if eventFilters or eventGroupBys exist
    const eventFiltersExists: boolean = this.eventFilters && this.eventFilters.length > 0;
    const eventGroupByExists: boolean =
      groupBy && groupBy.length > 0 && groupBy.filter((item) => !!item.event).length > 0;
    if (this.funnelAttributionSelection && (eventGroupByExists || eventFiltersExists)) {
      params.action_mode = this.funnelAttributionSelection.action_mode;
      if (this.funnelAttributionSelection.action_mode === `step`) {
        params.action_step = this.funnelAttributionSelection.action_step;
      }
    }

    if (this.version === 2 && extraParams) {
      params.eventIndex = extraParams.eventIndex;
      params.trendType = extraParams.trendType;
      params.frequencyType = extraParams.frequencyType;
      params.frequencyVizType = extraParams.frequencyVizType;
      params.ttcVizType = extraParams.ttcVizType;
      params.ttcValueRepresentation = extraParams.ttcValueRepresentation;
      params.frequencyValueRepresentation = extraParams.frequencyValueRepresentation;
      params.sortField = extraParams.sortField;
      params.sortOrder = extraParams.sortOrder;
    }

    if (this.isAnyOrderFunnel()) {
      params.is_step_any_order = this.events.map((entry) => entry.entryIndexLabelToggle || false);
    }

    if (!isEmpty(groupBy)) {
      params.group_by = groupBy;
    }

    // TODO: Remove userFilters length check once we switch to the new API
    if (this.eventFilters.length > 0 || this.userFilters.length > 0) {
      params.filter_by_event = this.getFilterByEvent();
    }

    return params;
  }

  public getFilterByEvent() {
    // TODO: Remove userFilter merge from this once we switch over to the new API
    return {
      operator: `and`,
      children: [
        ...this.eventFilters.map((filter) => ({event: filter})),
        ...this.userFilters.map((filter) => ({user: filter})),
      ],
    };
  }

  public isAnyOrderFunnel() {
    return this.funnelOrder === FunnelOrder.Any;
  }

  public isSegmentedByTopPaths() {
    return this.groupBy && this.groupBy.length > 0 && this.groupBy.findIndex(isOtherSegment) >= 0;
  }

  public isGroupedSteps(): boolean {
    return this.events.some((event) => isGroupedEntry(event));
  }

  public isSegmented(): boolean {
    return this.groupBy.length > 0;
  }

  /** Is the funnel query "complete" and executable? i.e. more than one step */
  public isValid() {
    return this.events.length > 1;
  }

  public getStepLabels(ignoreAnyOrder = false) {
    if (!ignoreAnyOrder && this.isAnyOrderFunnel()) {
      const labels = Array(this.events.length);
      for (let i = 0; i < this.events.length; i++) {
        labels[i] = `Step ${i + 1}`;
      }
      return labels;
    } else {
      return this.events.map((step, index) => {
        const {event} = step;
        if (Array.isArray(event)) {
          return range(0, event.length)
            .map((idx) => `${index + 1}${getAlphaLabelFromIndex(idx)}`)
            .join(` or `);
        } else {
          return event.renamed || event.label;
        }
      });
    }
  }

  public getDefaultAnyOrderSteps() {
    const events = this.events.map((entry) => ({...entry, entryIndexLabelToggle: true}));
    if (events.length > 0) {
      events[0].entryIndexLabelToggle = false;
    }
    return events;
  }

  /** return properties necessary for arb-selector conversion of property filters */
  private getPropertySerializerConfig(): SerializePropFilterContext {
    return {
      topLevelDataGroupId: this.dataGroupId,
      projectId: this.projectId,
      utcOffset: this.utcOffset,
    };
  }
}

/** convert property filter attributes into PropertyFilter instances */
function constructPropertyFilters(filterPrimitives: QuerySuperEntry['filters']) {
  return filterPrimitives.map((filter) => new PropertyFilter(filter));
}
